import React from "react";
import axios from "axios";
import Modal from "../../../../shared/components/Modal/Modal";
import { url_290 } from "../../../../custom_utilities/api_services";
import images from "../../../../utilities/images/images";
import "./StreakCalendarModal.css";

const WEEK_DAYS = ["S", "M", "T", "W", "T", "F", "S"];

const pad2 = (n) => String(n).padStart(2, "0");

const getMonthTitle = (year, month1) => {
  const dt = new Date(year, month1 - 1, 1);
  const label = dt.toLocaleString("en-US", { month: "long" });
  return `${label} ${year}`;
};

const getFirstWeekday = (year, month1) => new Date(year, month1 - 1, 1).getDay();
const getDaysInMonth = (year, month1) => new Date(year, month1, 0).getDate();

function StreakCalendarModal({ isOpen, onClose }) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const [viewYear, setViewYear] = React.useState(currentYear);
  const [viewMonth, setViewMonth] = React.useState(currentMonth);
  const [calendarData, setCalendarData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [tooltipKey, setTooltipKey] = React.useState(null);

  React.useEffect(() => {
    if (!isOpen) return;
    const fetchMonthCalendar = async () => {
      setLoading(true);
      try {
        const response = await axios.get(url_290, {
          params: { year: viewYear, month: viewMonth },
        });
        if (response?.data?.status === 200) {
          setCalendarData(response.data.data);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchMonthCalendar();
  }, [isOpen, viewYear, viewMonth]);

  const isAtCurrentMonth =
    viewYear > currentYear || (viewYear === currentYear && viewMonth >= currentMonth);

  const onPrev = () => {
    setTooltipKey(null);
    setViewMonth((m) => {
      if (m === 1) {
        setViewYear((y) => y - 1);
        return 12;
      }
      return m - 1;
    });
  };

  const onNext = () => {
    if (isAtCurrentMonth) return;
    setTooltipKey(null);
    setViewMonth((m) => {
      if (m === 12) {
        setViewYear((y) => y + 1);
        return 1;
      }
      return m + 1;
    });
  };

  const daysByDate = React.useMemo(() => {
    const map = new Map();
    (calendarData?.days || []).forEach((d) => {
      if (d?.date) map.set(d.date, d);
    });
    return map;
  }, [calendarData]);

  const gridRows = React.useMemo(() => {
    const firstWeekday = getFirstWeekday(viewYear, viewMonth);
    const totalDays = getDaysInMonth(viewYear, viewMonth);
    const cells = [];

    for (let i = 0; i < firstWeekday; i++) cells.push({ key: `pad-${i}` });

    for (let dayNum = 1; dayNum <= totalDays; dayNum++) {
      const date = `${viewYear}-${pad2(viewMonth)}-${pad2(dayNum)}`;
      cells.push({ key: date, dayNum, data: daysByDate.get(date) });
    }

    while (cells.length % 7 !== 0) cells.push({ key: `pad-tail-${cells.length}` });

    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    return rows;
  }, [daysByDate, viewYear, viewMonth]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeAboveModal
      stackClassName="streak-calendar-modal__dialog-stack"
      width="100%"
      height="auto"
      maxHeight="min(85vh, 720px)"
      borderRadius="0.875rem"
      marginTop="0"
      padding="clamp(0.45rem, 1.8vw, 0.65rem) clamp(0.45rem, 2.2vw, 0.75rem)"
      overflowY="auto"
      overflowX="hidden"
    >
      <div className="streak-calendar">
        <div className="streak-calendar__header">
          <button type="button" className="streak-calendar__nav-btn" onClick={onPrev} aria-label="Previous month">
            <img src={images.right_angle_icon} alt="" className="streak-calendar__nav-icon streak-calendar__nav-icon--left" />
          </button>
          <h3 className="streak-calendar__title">{getMonthTitle(viewYear, viewMonth)}</h3>
          <button
            type="button"
            className="streak-calendar__nav-btn"
            onClick={onNext}
            aria-label="Next month"
            disabled={isAtCurrentMonth}
          >
            <img src={images.right_angle_icon} alt="" className="streak-calendar__nav-icon" />
          </button>
        </div>

        <div className="streak-calendar__week-row">
          {WEEK_DAYS.map((day, idx) => (
            <div key={`${day}-${idx}`} className="streak-calendar__week-cell">
              {day}
            </div>
          ))}
        </div>

        {loading ? (
          <div className="streak-calendar__loading">Loading...</div>
        ) : (
          <div className="streak-calendar__grid">
            {gridRows.map((row, rowIdx) => (
              <div key={`row-${rowIdx}`} className="streak-calendar__grid-row">
                {row.map((cell) => {
                  if (!cell.dayNum) return <div key={cell.key} className="streak-calendar__pad-cell" />;

                  const data = cell.data || {};
                  const status = data?.streak_status || "not_started";
                  const isCompleted = status === "achived";
                  const isNotCompleted = status === "not_achived";
                  const isNotStarted = !isCompleted && !isNotCompleted;
                  const isToday = data?.is_today === true;
                  const hasTooltip = !!data?.tooltip;
                  const isTooltipOpen = tooltipKey === cell.key && hasTooltip;

                  return (
                    <button
                      type="button"
                      key={cell.key}
                      className={`streak-calendar__day-cell${
                        isCompleted
                          ? " streak-calendar__day-cell--completed"
                          : isNotCompleted
                            ? " streak-calendar__day-cell--not-completed"
                            : " streak-calendar__day-cell--not-started"
                      }${isToday ? " streak-calendar__day-cell--today" : ""}`}
                      onClick={() => setTooltipKey((prev) => (prev === cell.key ? null : cell.key))}
                    >
                      {isTooltipOpen ? (
                        <div className="streak-calendar__tooltip">
                          <span className="streak-calendar__tooltip-text">
                            {typeof data.tooltip === "string"
                              ? data.tooltip.replace(/\s+/g, " ").trim()
                              : data.tooltip}
                          </span>
                        </div>
                      ) : null}
                      <span className="streak-calendar__day-num">{cell.dayNum}</span>
                      <span className="streak-calendar__day-value">
                        {data?.display_value !== null && data?.display_value !== undefined ? data.display_value : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        <div className="streak-calendar__legend">
          <span className="streak-calendar__legend-item">
            <i className="streak-calendar__swatch streak-calendar__swatch--not-started" />
            Not Started
          </span>
          <span className="streak-calendar__legend-item">
            <i className="streak-calendar__swatch streak-calendar__swatch--completed" />
            Completed
          </span>
          <span className="streak-calendar__legend-item">
            <i className="streak-calendar__swatch streak-calendar__swatch--not-completed" />
            Not Completed
          </span>
        </div>
      </div>
    </Modal>
  );
}

export default StreakCalendarModal;

